import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { enumAttribute, primitiveEvent, primitiveStyles } from '../shared/primitive-utils.js';
import { statusSeverities, statusStyles, type StatusSeverity } from '../shared/status-styles.js';

/**
 * Text tag with optional selection and removal. Removal is requested; the application owns the data.
 * @element qds-tag
 * @attr {string} label - Text fallback and accessible removal subject.
 * @attr {'neutral'|'info'|'success'|'warning'|'error'} severity - Semantic color.
 * @attr {boolean} selectable - Enable toggle-button semantics for the label.
 * @attr {boolean} selected - Selected state when selectable.
 * @attr {boolean} removable - Display the remove action.
 * @attr {boolean} disabled - Disable selection and removal.
 * @attr {string} remove-label - Explicit accessible remove name.
 * @property {boolean} selectable - Selection is enabled.
 * @property {boolean} selected - Selection state.
 * @property {boolean} removable - Removal action is available.
 * @property {boolean} disabled - Interaction is disabled.
 * @property {string} severity - Semantic color.
 * @slot - Tag label; use noninteractive content.
 * @csspart tag - Tag surface.
 * @csspart label - Optional toggle action.
 * @csspart remove - Native remove button.
 * @fires {{ component: HTMLElement; selected: boolean }} qds-change - User toggled selection. Detail: { component, selected }.
 * @fires {{ component: HTMLElement }} qds-remove - Cancelable remove request. Detail: { component }; application removes the element.
 */
export class QdsTag extends HTMLElement {
  static get observedAttributes(): string[] { return ['label', 'severity', 'selectable', 'selected', 'removable', 'disabled', 'remove-label']; }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}${statusStyles}
      :host { display: inline-flex; vertical-align: middle; }
      [part="tag"] { display: inline-flex; align-items: center; max-width: 100%; border: 1px solid var(--theme-border-primary, #e4e9f2);
        border-radius: 4px; color: var(--_status-fg); background: var(--_status-bg); }
      [part="label"] { display: inline-flex; align-items: center; padding: 4px 8px; overflow-wrap: anywhere; }
      [part="label"][role="button"] { min-width: 44px; min-height: 44px; justify-content: center; cursor: pointer; }
      [part="label"][aria-pressed="true"] { box-shadow: inset 0 -3px currentColor; font-weight: 600; }
      [part="label"]:focus-visible { outline: 2px solid var(--theme-accent-primary, #445e89); outline-offset: 2px; }
      button { min-width: 44px; min-height: 44px; border: 0; border-inline-start: 1px solid currentColor; background: transparent; color: inherit; cursor: pointer; font-size: 20px; }
      :host([disabled]) [role="button"], button:disabled { cursor: default; }
      @media (forced-colors: active) { [part="tag"] { border-color: CanvasText; } }
    </style><span part="tag"><span part="label"><slot><span class="fallback"></span></slot></span>
      <button part="remove" type="button"><span aria-hidden="true">×</span></button></span>`;
    loadBaseCSS();
  }
  connectedCallback(): void {
    for (const prop of ['selectable', 'selected', 'removable', 'disabled', 'severity']) upgradeProperty(this, prop);
    this.shadowRoot!.addEventListener('click', this._onClick);
    this.shadowRoot!.addEventListener('keydown', this._onKeydown as EventListener);
    this.shadowRoot!.querySelector('slot')!.addEventListener('slotchange', this._onSlot);
    this._sync();
  }
  disconnectedCallback(): void {
    this.shadowRoot!.removeEventListener('click', this._onClick);
    this.shadowRoot!.removeEventListener('keydown', this._onKeydown as EventListener);
    this.shadowRoot!.querySelector('slot')!.removeEventListener('slotchange', this._onSlot);
  }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  get selectable(): boolean { return this.hasAttribute('selectable'); }
  set selectable(value: boolean) { this.toggleAttribute('selectable', !!value); }
  get selected(): boolean { return this.hasAttribute('selected'); }
  set selected(value: boolean) { this.toggleAttribute('selected', !!value); }
  get removable(): boolean { return this.hasAttribute('removable'); }
  set removable(value: boolean) { this.toggleAttribute('removable', !!value); }
  get disabled(): boolean { return this.hasAttribute('disabled'); }
  set disabled(value: boolean) { this.toggleAttribute('disabled', !!value); }
  get severity(): StatusSeverity { return enumAttribute(this, 'severity', statusSeverities, 'neutral'); }
  set severity(value: StatusSeverity) { this.setAttribute('severity', value); }
  private _sync(): void {
    const label = this.shadowRoot!.querySelector<HTMLElement>('[part="label"]')!;
    this.shadowRoot!.querySelector('.fallback')!.textContent = this.getAttribute('label') || '';
    if (this.selectable) { label.setAttribute('role', 'button'); label.setAttribute('aria-pressed', String(this.selected)); label.setAttribute('aria-disabled', String(this.disabled)); label.tabIndex = this.disabled ? -1 : 0; }
    else { for (const attr of ['role', 'aria-pressed', 'aria-disabled', 'tabindex']) label.removeAttribute(attr); }
    const button = this.shadowRoot!.querySelector('button')!;
    button.hidden = !this.removable; button.disabled = this.disabled;
    button.setAttribute('aria-label', this.getAttribute('remove-label') || `Remove ${this.getAttribute('label') || this.textContent?.trim() || 'tag'}`);
  }
  private _toggle(): void {
    if (this.disabled || !this.selectable) return;
    this.selected = !this.selected;
    primitiveEvent(this, 'qds-change', { component: this, selected: this.selected });
  }
  private _onClick = (event: Event): void => {
    if (this.disabled) return;
    if (event.composedPath().includes(this.shadowRoot!.querySelector('button')!)) {
      if (this.removable) primitiveEvent(this, 'qds-remove', { component: this }, true);
    } else if (event.composedPath().includes(this.shadowRoot!.querySelector('[part="label"]')!)) this._toggle();
  };
  private _onKeydown = (event: KeyboardEvent): void => {
    if (event.target !== this.shadowRoot!.querySelector('[part="label"]') || !this.selectable || this.disabled || event.repeat) return;
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this._toggle(); }
  };
  private _onSlot = (): void => { this._sync(); };
}
if (!customElements.get('qds-tag')) customElements.define('qds-tag', QdsTag);
